import { downloadDocuments } from './document-downloads';

/**
 * A job's exported documents, saved from its Download destination: the documents the ledger delivered to
 * Download (`GET /v1/jobs/{id}/exports?destination=download&state=delivered`, newest first), each package
 * once, in one zip (document-downloads.ts). Until 2026-10-11 only the sample tour could save its documents;
 * a customer's own job with Download as its only destination had no way to get its files out of Paige
 * (first-run walk).
 */
export const LATEST = 200;

interface ExportRow {
  documentId: string;
  documentState?: string;
}

/** The documents delivered to Download, newest first, each once; at most the latest {@link LATEST}. */
export async function exportedDocumentIds(
  jobChannel: string,
  tenant: string | undefined,
): Promise<string[]> {
  const query = `destination=download&state=delivered&limit=${LATEST}`;
  const response = await fetch(`${jobChannel}/exports?${query}`, {
    credentials: 'include',
    headers: tenant ? { 'Paige-Tenant': tenant } : {},
  });
  if (!response.ok) throw new Error('Paige could not list this job’s exported documents.');
  const { exports } = (await response.json()) as { exports: ExportRow[] };
  return uniqueDocuments(exports);
}

/** Each document once, in the order first seen; a document sent back to review is left out. */
export function uniqueDocuments(rows: readonly ExportRow[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    if (row.documentState && row.documentState !== 'exported') continue;
    seen.add(row.documentId);
  }
  return [...seen];
}

/** Saves the job's exported documents as one zip; how many went in (0: nothing exported yet, nothing saved). */
export async function downloadExported(
  jobChannel: string,
  apiRoot: string,
  tenant: string | undefined,
  zipName: string,
): Promise<number> {
  const ids = await exportedDocumentIds(jobChannel, tenant);
  if (ids.length > 0) await downloadDocuments(ids, zipName, { apiOrigin: apiRoot, tenant });
  return ids.length;
}
